import React, { useEffect, useRef, useMemo, useCallback } from "react";
import { useReactFlow, useEdges } from "reactflow";
import { useLoopDetection } from "../util/useLoopDetection";
import { getEdgeType } from "../util/myFunctionsCoreDisplay";

interface LoopDetectionWrapperProps {
  children: React.ReactNode;
}

const LoopDetectionWrapper: React.FC<LoopDetectionWrapperProps> = ({
  children,
}) => {
  const { setEdges } = useReactFlow();
  const edges = useEdges();
  const { loopInfo, isEdgeInLoop, getEdgeLoopType } = useLoopDetection();
  const previousEdgesCount = useRef<number>(0);
  const previousEdgesStructure = useRef<string>("");
  const previousLoopInfo = useRef<{ isLoop: boolean; loopEdges: string[] }>({
    isLoop: false,
    loopEdges: [],
  });
  const loopEdgeStyles = useRef<Map<string, any>>(new Map());
  const lastAppliedEdges = useRef<string>("");

  const currentEdgesStructure = useMemo(() => {
    return edges
      .map(
        (edge) =>
          `${edge.id}-${edge.source}-${edge.target}-${edge.sourceHandle}-${edge.targetHandle}`,
      )
      .sort()
      .join(",");
  }, [edges]);

  const currentLoopInfo = useMemo(
    () => ({
      isLoop: loopInfo.isLoop,
      loopEdges: loopInfo.loopEdges,
    }),
    [loopInfo.isLoop, loopInfo.loopEdges],
  );

  const getLoopStyle = useCallback(
    (edge: any, isInLoop: boolean, loopType: string | null) => {
      if (!isInLoop) return null;

      const isFlowConnection =
        (edge.sourceHandle === "outputFlow" ||
          edge.sourceHandle === "output" ||
          edge.sourceHandle === "outputHandle-0" ||
          edge.sourceHandle === "outputHandle-1" ||
          edge.sourceHandle === "true" ||
          edge.sourceHandle === "false" ||
          edge.sourceHandle === "a") &&
        (edge.targetHandle === "input" ||
          edge.targetHandle === "inputFlow" ||
          edge.targetHandle === "inputHandle-0" ||
          edge.targetHandle === "inputHandle-1");

      if (!isFlowConnection) {
        return null;
      }

      return {
        ...edge,
        type: "loopEdge",
        data: {
          ...edge.data,
          isLoop: true,
          loopType: loopType,
        },
      };
    },
    [],
  );

  const applyLoopStyles = useCallback(
    (edges: any[]) => {
      return edges.map((edge) => {
        const isInLoop = isEdgeInLoop(edge.id);
        const loopType = getEdgeLoopType(edge.id);

        if (isInLoop) {
          const loopStyle = getLoopStyle(edge, isInLoop, loopType);

          if (loopStyle) {
            loopEdgeStyles.current.set(edge.id, loopStyle);

            return {
              ...edge,
              ...loopStyle,
              data: {
                ...edge.data,
                isLoop: true,
                loopType: loopType,
              },
            };
          }
        } else {
          loopEdgeStyles.current.delete(edge.id);

          if (edge.type === "loopEdge") {
            return {
              ...edge,
              type: getEdgeType(edge.sourceHandle || ""),
              data: {
                ...edge.data,
                isLoop: false,
                loopType: null,
              },
            };
          }
        }
        return edge;
      });
    },
    [isEdgeInLoop, getEdgeLoopType, getLoopStyle],
  );

  useEffect(() => {
    const currentEdgesCount = edges.length;
    const currentEdgesHash = edges
      .map((edge) => `${edge.id}-${edge.source}-${edge.target}`)
      .sort()
      .join(",");

    const edgesThatShouldHaveLoopStyles = edges.filter((edge) => {
      const isInLoop = isEdgeInLoop(edge.id);
      if (!isInLoop || edge.type === "loopEdge") return false;

      const isFlowConnection =
        (edge.sourceHandle === "outputFlow" ||
          edge.sourceHandle === "output" ||
          edge.sourceHandle === "outputHandle-0" ||
          edge.sourceHandle === "outputHandle-1" ||
          edge.sourceHandle === "true" ||
          edge.sourceHandle === "false" ||
          edge.sourceHandle === "a") &&
        (edge.targetHandle === "input" ||
          edge.targetHandle === "inputFlow" ||
          edge.targetHandle === "inputHandle-0" ||
          edge.targetHandle === "inputHandle-1");

      return isFlowConnection;
    });

    const shouldUpdate =
      previousEdgesCount.current !== currentEdgesCount ||
      previousEdgesStructure.current !== currentEdgesStructure ||
      previousLoopInfo.current.isLoop !== currentLoopInfo.isLoop ||
      JSON.stringify(previousLoopInfo.current.loopEdges) !==
        JSON.stringify(currentLoopInfo.loopEdges) ||
      lastAppliedEdges.current !== currentEdgesHash ||
      edgesThatShouldHaveLoopStyles.length > 0;

    if (shouldUpdate) {
      previousEdgesCount.current = currentEdgesCount;
      previousEdgesStructure.current = currentEdgesStructure;
      previousLoopInfo.current = currentLoopInfo;
      lastAppliedEdges.current = currentEdgesHash;

      const updatedEdges = applyLoopStyles(edges);
      setEdges(updatedEdges);
    }
  }, [
    edges,
    currentEdgesStructure,
    currentLoopInfo,
    setEdges,
    applyLoopStyles,
    isEdgeInLoop,
  ]);

  return <>{children}</>;
};

export default LoopDetectionWrapper;
